import { SegmentFilter } from '../segmentFilters';
import { StatItemProps } from './SegmentItem';
import { SegmentedBar, SingleStat, StatRow, WIDGET_COLORS, WidgetCard, WidgetTooltip, profilingIcon } from './WidgetPrimitives';

// The profile_status_for_required_users ransacker matches only the educators whose employment
// type requires profiling, which are the ones counted here.
// Shared by the bar segment and the stat below it so both toggle the same filter.
const profileFilter = (status: string, label: string): SegmentFilter => ({
  attribute: 'profile_status_for_required_users',
  value: status,
  group: 'Profile Completion',
  label
});

const profileStat = (
  percent: number,
  status: string,
  label: string,
  color?: string
): StatItemProps => ({
  value: `${percent}%`,
  label,
  color,
  filter: profileFilter(status, label)
});

export interface ProfileCompletionWidgetData {
  total_count?: number;
  supervisor_actioning_average_days?: number;
  counts?: {
    complete?: number;
    educator_complete?: number;
    incomplete?: number;
  };
  percentages?: {
    complete?: number;
    educator_complete?: number;
    incomplete?: number;
  };
}

interface ProfileCompletionWidgetProps {
  data: ProfileCompletionWidgetData;
}

const TOOLTIP = (
  <WidgetTooltip
    heading="Profile Completion"
    description="Shows progress towards completion of the Profiling Tool."
    definitions={[
      {
        term: 'Completed',
        description: 'Educators who have completed the Profiling Tool, including supervisor approval where required.',
        color: WIDGET_COLORS.green
      },
      {
        term: 'Educator Completed',
        description:
          'Educators who have submitted their profile and are awaiting supervisor approval. This step is skipped if approval is not required.',
        color: WIDGET_COLORS.blue
      },
      {
        term: 'Incomplete',
        description: 'Educators who have not started a profile during the current calendar year.'
      }
    ]}
  />
);

const ProfileCompletionWidget = ({
  data: {
    total_count: total = 0,
    supervisor_actioning_average_days: avgSupervisorCompletionDays,
    counts: {
      complete: completedCount = 0,
      educator_complete: educatorCompletedCount = 0,
      incomplete: incompleteCount = 0
    } = {},
    percentages: {
      complete: completedPercent = 0,
      educator_complete: educatorCompletedPercent = 0,
      incomplete: incompletePercent = 0
    } = {}
  }
}: ProfileCompletionWidgetProps) => {

  return (
    <WidgetCard title="Profile Completion" total={total} icon={profilingIcon} tooltip={TOOLTIP}>
      <SegmentedBar
        segments={[
          {
            percent: completedPercent,
            color: WIDGET_COLORS.green,
            label: 'Completed',
            count: completedCount,
            filter: profileFilter('complete', 'Completed')
          },
          {
            percent: educatorCompletedPercent,
            color: WIDGET_COLORS.blue,
            label: 'Educator completed',
            count: educatorCompletedCount,
            filter: profileFilter('educator_complete', 'Educator completed')
          }
        ]}
        remainder={{
          label: 'Incomplete',
          percent: incompletePercent,
          count: incompleteCount,
          filter: profileFilter('incomplete', 'Incomplete')
        }}
      />
      <StatRow
        items={[
          profileStat(completedPercent, 'complete', 'Completed', WIDGET_COLORS.green),
          profileStat(educatorCompletedPercent, 'educator_complete', 'Educator completed', WIDGET_COLORS.blue),
          profileStat(incompletePercent, 'incomplete', 'Incomplete')
        ]}
      />
      {avgSupervisorCompletionDays != null ? (
        <SingleStat value={`${avgSupervisorCompletionDays}`} label="days avg supervisor completion" />
      ) : null}
    </WidgetCard>
  )
};

export default ProfileCompletionWidget;
